import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ShieldCheck, Truck, Star, ChevronRight, BadgeCheck,
  Users, MapPin, ChevronDown, ChevronUp,
  UtensilsCrossed, Clock, HandHeart, Droplets, Globe2, PackageCheck, ShowerHead, Car, Lock } from "lucide-react";
import BrandHeader from "@/components/BrandHeader";
import NewsLogos from "@/components/NewsLogos";
import FloatingCallButton from "@/components/FloatingCallButton";
import Avatar from "@/components/Avatar";
import CleaningDifference from "@/components/CleaningDifference";
import { REVIEWS } from "@/lib/reviews";
import { fireCapi } from "@/lib/pixel-events";
import { getDiwali } from "@/lib/diwali";
import { getSrCheckoutConfig, loadSrSdk, openSrCheckout } from "@/lib/sr-checkout";

const PRICE = 499;
const MRP = 2399;
const SAVE = MRP - PRICE;

const Stars = ({ n = 5, size = 14 }) => (
  <div className="inline-flex gap-0.5 text-amber-500" aria-label={`${n} stars`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={size} fill={i < n ? "currentColor" : "none"} strokeWidth={1.8} />
    ))}
  </div>
);

const SectionTitle = ({ kicker, title, sub, center = true }) => (
  <div className={`mb-5 ${center ? "text-center" : ""}`}>
    {kicker && (
      <div className="inline-block text-[11px] tracking-[0.18em] font-semibold text-emerald-800 bg-emerald-100/70 rounded-full px-3 py-1 mb-2 uppercase">
        {kicker}
      </div>
    )}
    <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0a1a14] leading-tight">{title}</h2>
    {sub && <p className="text-sm text-slate-600 mt-1.5 max-w-md mx-auto">{sub}</p>}
  </div>
);

/* ------------------------- Hero (static — no slider) ------------------------- */
const SecureBySr = ({ show, dark }) => show ? (
  <div data-testid="secure-by-shiprocket" className={`mt-2 flex items-center justify-center gap-1.5 text-[11px] ${dark ? "text-emerald-100/80" : "text-slate-500"}`}>
    <Lock size={11} className={dark ? "text-emerald-200" : "text-emerald-700"} />
    <span>Secure Checkout By</span>
    <img src="/assets/shiprocket-logo.png" alt="" width="14" height="15" className={`h-[15px] w-auto ${dark ? "bg-white rounded-sm p-[1px]" : ""}`} />
    <span className={`font-semibold ${dark ? "text-white" : "text-slate-700"}`}>Shiprocket</span>
  </div>
) : null;

const Hero = ({ onOrderClick, srMode }) => {
  const diwali = getDiwali();
  return (
    <section data-testid="hero" className="px-4 pt-4 pb-2">
      <div className="card-soft p-4 relative overflow-hidden m2s-grain">
        {diwali.active && (
          <div
            className="absolute top-3 left-3 right-3 z-10 text-center text-[11.5px] font-extrabold rounded-full px-3 py-1.5"
            style={{ background: "linear-gradient(90deg,#7a1f12 0%,#a3341a 50%,#7a1f12 100%)", color: "#ffe08a" }}
            data-testid="hero-diwali-ribbon"
          >
            <span className="m2s-diya-glow">🪔</span> Diwali ki safai shuru ho gayi? ✨ Ab ghar chamkega{" "}
            <span className="m2s-diya-glow">🪔</span>
          </div>
        )}

        <div className="relative aspect-square w-full overflow-hidden rounded-xl bg-emerald-50">
          <img
            src="/assets/product/hero-main.webp"
            alt="Multifunctional Japanese Cleaner — Buy 1 Get 3 FREE"
            width="800"
            height="800"
            fetchPriority="high"
            loading="eager"
            className="w-full h-full object-contain m2s-float"
          />
        </div>

        <div className="mt-3 text-center">
          <h1 data-testid="hero-headline" className="font-display text-[26px] sm:text-3xl font-extrabold text-emerald-900 leading-[1.1]">
            Daag, Tel, Chikki Mail?<br />
            <span className="bg-amber-300 text-emerald-900 px-1.5 rounded-md">Sirf 5 Min</span> me Sparkling Clean
          </h1>
          <p className="text-sm text-slate-700 mt-2 leading-snug">
            🇯🇵 Original Japanese Multi-Cleaner • Kitchen, Bathroom, Car, Tiles, Glass — sab ek hi bottle se. <b>Buy 1 Get 3 FREE</b>.
          </p>
        </div>

        <div className="mt-3 flex items-center justify-center gap-3">
          <div className="text-slate-400 line-through text-base font-semibold">₹{MRP}</div>
          <div className="text-3xl font-display font-extrabold text-emerald-900">₹{PRICE}</div>
          <div className="bg-emerald-100 text-emerald-800 text-[11px] font-bold rounded-full px-2 py-0.5">79% OFF</div>
        </div>

        <button
          data-testid="hero-cta"
          onClick={onOrderClick}
          className="btn-primary w-full mt-3 text-lg flex items-center justify-center gap-2 m2s-cta-shine"
        >
          Order Now <ChevronRight size={20} />
        </button>
        <SecureBySr show={srMode} />
        <a href="#real-reviews" data-testid="hero-reviews-link" className="hidden">
          Check Real Customer Reviews ↓
        </a>
      </div>
    </section>
  );
};

/* ------------------------- Video — autoplay muted ------------------------- */
const ExplainerVideo = () => {
  const ref = useRef(null);
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) setMounted(true); });
    }, { rootMargin: "200px" });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <section ref={ref} className="px-4 mt-5" data-testid="explainer-video">
      <SectionTitle kicker="Watch now" title="Live Demo" sub="Real product, real shine — Hindi me explainer" />
      <div className="card-soft overflow-hidden">
        <div className="relative aspect-[9/16] bg-emerald-950">
          {mounted ? (
            <video
              src="/assets/videos/explainer.mp4"
              autoPlay
              muted
              loop
              playsInline
              preload="none"
              poster="/assets/videos/explainer_poster.webp"
              className="w-full h-full object-cover"
              controls
            />
          ) : (
            <img
              src="/assets/videos/explainer_poster.webp"
              alt="Live demo"
              width="540"
              height="960"
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
        </div>
      </div>
    </section>
  );
};

/* ------------------------- Pack box ------------------------- */
const PackBox = ({ onOrderClick, srMode }) => (
  <section data-testid="pack-box" className="px-4 mt-5">
    <SectionTitle kicker="Aaj ka mega offer" title="Buy 1 Get 3 FREE" sub="Total 4 Bottles — keval ₹499" />
    <div className="card-soft p-4 border-2 border-emerald-300 relative m2s-pulse">
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-red-600 text-white text-[11px] font-extrabold rounded-full px-3 py-1 shadow-md">
        🔥 MOST POPULAR
      </div>
      <div className="flex items-start gap-3">
        <img src="/assets/product/hero-main.webp" alt="4 Bottles" width="120" height="120" className="w-24 h-24 object-contain rounded-lg bg-emerald-50" loading="lazy" decoding="async" />
        <div className="flex-1">
          <div className="text-emerald-900 font-display font-extrabold text-lg leading-tight">1 + 3 Bottles FREE</div>
          <div className="text-[12px] text-slate-600">Multifunctional Japanese Cleaner — 4 × 300g</div>
          <div className="mt-1.5 flex items-baseline gap-2">
            <div className="text-2xl font-extrabold text-emerald-900">₹{PRICE}</div>
            <div className="text-slate-400 line-through text-sm">₹{MRP}</div>
          </div>
          <div className="text-[11px] mt-0.5 font-bold text-emerald-700">You Save ₹{SAVE.toLocaleString("en-IN")} (79% OFF)</div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3 text-[10.5px] text-center">
        <div className="bg-emerald-50 rounded-lg py-1.5 border border-emerald-100 font-semibold text-emerald-900">FREE Delivery</div>
        <div className="bg-emerald-50 rounded-lg py-1.5 border border-emerald-100 font-semibold text-emerald-900">COD Available</div>
        <div className="bg-emerald-50 rounded-lg py-1.5 border border-emerald-100 font-semibold text-emerald-900">7-Day Refund</div>
      </div>
      <button data-testid="pack-cta" onClick={onOrderClick} className="btn-primary w-full mt-3 text-lg flex items-center justify-center gap-2 m2s-cta-shine">
        Claim Your 4 Bottles @ ₹{PRICE} <ChevronRight size={20} />
      </button>
      <SecureBySr show={srMode} />
    </div>
  </section>
);

const GuaranteeBar = () => (
  <div className="px-4 mt-3">
    <div className="bg-amber-100 border border-amber-300 rounded-xl px-3 py-2.5 flex items-center gap-2.5">
      <ShieldCheck className="text-amber-700 shrink-0" size={22} />
      <div className="text-[12.5px] leading-snug">
        <b className="text-amber-900">Result Nahi Mile? 100% Paisa Wapas</b> — 7 Day Money Back Promise
      </div>
    </div>
  </div>
);

const DeliveryEstimate = () => {
  const formatDate = (d) => d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
  const today = new Date();
  let target = new Date(today);
  let added = 0;
  while (added < 4) {
    target.setDate(target.getDate() + 1);
    if (target.getDay() !== 0) added++;
  }
  return (
    <div className="px-4 mt-2.5">
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2.5 flex items-center gap-2.5">
        <Truck className="text-emerald-700 shrink-0" size={20} />
        <div className="text-[12.5px] leading-snug">
          <b className="text-emerald-900">FREE Delivery by {formatDate(target)}</b>
          <span className="text-emerald-800"> • COD on Delivery, no advance</span>
        </div>
      </div>
    </div>
  );
};

const AuthorizedSeller = () => (
  <section className="px-4 mt-3" data-testid="official-store-badge">
    <div className="bg-white border border-emerald-200 rounded-xl p-3 flex items-center gap-2.5">
      <div className="w-9 h-9 rounded-full bg-emerald-600 text-white grid place-items-center shrink-0">
        <BadgeCheck size={20} />
      </div>
      <div className="text-[12.5px] leading-snug">
        <div className="font-extrabold text-emerald-900">Official make2shine® Store</div>
        <div className="text-slate-600">100% genuine product · Sold &amp; shipped directly by the brand · Registered trademark</div>
      </div>
    </div>
  </section>
);

const SoldStat = () => {
  const [sold] = useState(() => 1240 + Math.floor(Math.random() * 60));
  return (
    <section className="px-4 mt-3">
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-2.5">
        <Users className="text-emerald-700" size={20} />
        <div className="text-[12.5px]">
          <b className="text-emerald-900">{sold.toLocaleString("en-IN")} packs ordered</b> in last 24 hours
        </div>
        <span className="ml-auto inline-flex h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
      </div>
    </section>
  );
};

/* ------------------------- Benefits with PROPER icons ------------------------- */
const Benefits = () => {
  const items = [
    { Icon: UtensilsCrossed, t: "Kitchen Cleaning", d: "Gas stove, sink, chimney — sab ek hi bottle se" },
    { Icon: Clock, t: "5 Min me Result", d: "Spray karo, ruko, dho dalo — done!" },
    { Icon: HandHeart, t: "Skin-Safe Formula", d: "Bina dastane safe — soft hands" },
    { Icon: ShowerHead, t: "Bathroom Shine", d: "Tiles, basin, toilet, taps sparkling" },
    { Icon: Droplets, t: "Glass & Mirror", d: "Streak-free shine instantly" },
    { Icon: Car, t: "Multi-Surface", d: "Car interior, plastic, steel — all" },
    { Icon: Globe2, t: "Indian Climate", d: "Hard water & dust tested" },
    { Icon: PackageCheck, t: "Family Pack", d: "4 bottles = 6 months ka stock" },
  ];
  return (
    <section className="px-4 mt-5" data-testid="benefits">
      <SectionTitle kicker="Kyun yahi cleaner" title="Sirf ek bottle — anginat fayde" />
      <div className="grid grid-cols-2 gap-2.5">
        {items.map(({ Icon, t, d }, i) => (
          <div key={i} className="card-soft p-3 m2s-reveal" style={{ animationDelay: `${i * 60}ms` }}>
            <div className="w-10 h-10 rounded-xl bg-emerald-100 grid place-items-center text-emerald-700 mb-1.5">
              <Icon size={20} strokeWidth={2.2} />
            </div>
            <div className="font-extrabold text-[13px] text-emerald-900 leading-tight">{t}</div>
            <div className="text-[11px] text-slate-600 leading-snug mt-0.5">{d}</div>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ------------------------- "How product looks like" gallery removed ------------------------- */

const Comparison = () => {
  const rows = [
    ["Multi-surface cleaning", true, false, false],
    ["5-minute results", true, false, false],
    ["No harmful chemicals", true, false, false],
    ["Skin-safe formula", true, false, false],
    ["Easy on hands (no gloves)", true, false, false],
    ["Removes tough oil & grease", true, true, false],
    ["Long-lasting bottle", true, false, true],
    ["Value for money (₹/use)", true, false, false],
  ];
  return (
    <section className="px-4 mt-5" data-testid="comparison">
      <SectionTitle kicker="Comparison" title="make2shine® vs Aam Cleaners" />
      <div className="card-soft overflow-hidden">
        <div className="grid grid-cols-4 bg-emerald-900 text-white text-[11px] font-bold">
          <div className="px-2 py-2.5">Feature</div>
          <div className="px-2 py-2.5 text-center bg-amber-400 text-emerald-900">make2shine®</div>
          <div className="px-2 py-2.5 text-center">Cif/Vim</div>
          <div className="px-2 py-2.5 text-center">Local Soap</div>
        </div>
        {rows.map(([t, a, b, c], i) => (
          <div key={i} className={`grid grid-cols-4 text-[11.5px] ${i % 2 ? "bg-emerald-50/40" : "bg-white"}`}>
            <div className="px-2 py-2 text-slate-800 font-semibold">{t}</div>
            <div className="px-2 py-2 text-center">{a ? "✅" : "❌"}</div>
            <div className="px-2 py-2 text-center text-slate-500">{b ? "✅" : "❌"}</div>
            <div className="px-2 py-2 text-center text-slate-500">{c ? "✅" : "❌"}</div>
          </div>
        ))}
      </div>
    </section>
  );
};

const HowToUse = () => {
  const steps = [
    { n: 1, t: "Spray karo", d: "Daag wali surface pe directly spray karo" },
    { n: 2, t: "1 minute ruko", d: "Cleaner ko apna kaam karne do" },
    { n: 3, t: "Pochha lagao", d: "Geele kapde se saaf karo — done!" },
  ];
  return (
    <section className="px-4 mt-5" data-testid="how-to-use">
      <SectionTitle kicker="Use karna asaan" title="3 Asaan Steps me Sparkling Clean" />
      <div className="grid grid-cols-3 gap-2">
        {steps.map((s) => (
          <div key={s.n} className="card-soft p-3 text-center">
            <div className="w-9 h-9 rounded-full bg-emerald-500 text-white grid place-items-center font-extrabold mx-auto">
              {s.n}
            </div>
            <div className="font-extrabold text-emerald-900 mt-1.5 text-[13px]">{s.t}</div>
            <div className="text-[10.5px] text-slate-600 mt-0.5 leading-snug">{s.d}</div>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ------------------------- How Our Package Looks Like ------------------------- */
const PackageReveal = () => (
  <section className="px-4 mt-5" data-testid="package-reveal">
    <SectionTitle
      kicker="Real unboxing"
      title="How Our Package Looks Like"
      sub="Sealed packaging, authentic product — exactly aisa hi aapke ghar aayega"
    />
    <div className="card-soft overflow-hidden">
      <div className="relative bg-emerald-50">
        <img
          src="/assets/product/package.webp"
          alt="make2shine® original sealed package — Multifunctional Japanese Cleaner"
          width="720"
          height="720"
          loading="lazy"
          decoding="async"
          className="w-full h-auto object-cover"
        />
        <div className="absolute top-3 left-3 inline-flex items-center gap-1 bg-emerald-700 text-white text-[10.5px] font-extrabold rounded-full px-2.5 py-1 shadow">
          <BadgeCheck size={13} /> Original • Sealed
        </div>
      </div>
      <div className="p-3 grid grid-cols-3 gap-2 text-center text-[11px]">
        <div className="bg-emerald-50 rounded-lg py-2 border border-emerald-100 font-semibold text-emerald-900">Tamper-proof</div>
        <div className="bg-emerald-50 rounded-lg py-2 border border-emerald-100 font-semibold text-emerald-900">Bubble-wrap</div>
        <div className="bg-emerald-50 rounded-lg py-2 border border-emerald-100 font-semibold text-emerald-900">QR-Verified</div>
      </div>
    </div>
  </section>
);

/* ------------------------- Reviews — text only, no count in load more ------------------------- */
const ReviewSection = () => {
  const [count, setCount] = useState(8);
  return (
    <section id="real-reviews" className="px-4 mt-6" data-testid="reviews">
      <SectionTitle kicker="Real customer voice" title="Verified Customer Reviews" sub="Tier 2 & 3 cities ke real buyers — no fake" />
      <div className="space-y-3">
        {REVIEWS.slice(0, count).map((r, i) => (
          <div key={`${r.name}-${r.city}-${r.daysAgo}-${i}`} className="card-soft p-3 m2s-reveal" style={{ animationDelay: `${(i % 4) * 80}ms` }}>
            <div className="flex items-start gap-2.5">
              <Avatar name={r.name} size={40} />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-extrabold text-emerald-900 text-[13px]">{r.name}</span>
                      {r.verified && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] bg-emerald-100 text-emerald-800 rounded-full px-1.5 py-0.5 font-bold">
                          <BadgeCheck size={10} /> Verified
                        </span>
                      )}
                    </div>
                    <div className="text-[10.5px] text-slate-500 flex items-center gap-1">
                      <MapPin size={10} /> {r.city} • {r.daysAgo}d ago
                    </div>
                  </div>
                  <Stars n={r.stars} size={12} />
                </div>
                <div className="text-[12.5px] text-slate-700 mt-1.5 leading-snug">&ldquo;{r.text}&rdquo;</div>
                {r.photo && (
                  <div className="mt-2">
                    <img
                      src={r.photo}
                      alt="customer attached"
                      width="112"
                      height="112"
                      loading="lazy"
                      decoding="async"
                      className="w-28 h-28 object-cover rounded-lg border border-slate-200"
                      data-testid={`review-photo-${i}`}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {count < REVIEWS.length && (
        <button
          onClick={() => setCount(count + 8)}
          data-testid="load-more-reviews"
          className="btn-secondary w-full mt-3"
        >
          Load More Reviews
        </button>
      )}
    </section>
  );
};

const FAQ = () => {
  const items = [
    { q: "Yeh original Japanese product hai kya?", a: "Haan, 100% original Japanese formula. make2shine® India ka official authorized importer hai." },
    { q: "COD available hai?", a: "Bilkul. Cash on Delivery available hai pure India me. Order karte waqt sirf address dijiye, paisa delivery par dijiye." },
    { q: "Delivery kitne din me?", a: "3–5 working days me delivery ho jati hai. Tier 2/3 cities me bhi same." },
    { q: "Refund policy kya hai?", a: "7-Day Money Back Guarantee. Agar result nahi mile, full refund." },
    { q: "Skin pe safe hai?", a: "Haan, non-toxic & skin-friendly formula hai. Bina dastane bhi use kar sakte ho." },
    { q: "Kahaan-kahaan use kar sakte hai?", a: "Kitchen, gas stove, sink, tiles, bathroom, glass, mirror, car interior, stainless steel — har jagah." },
  ];
  const [open, setOpen] = useState(0);
  return (
    <section className="px-4 mt-6" data-testid="faq">
      <SectionTitle kicker="FAQ" title="Aapke sawaal — hamare jawaab" />
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={it.q} className="card-soft overflow-hidden">
            <button
              onClick={() => setOpen(open === i ? -1 : i)}
              className="w-full px-3 py-2.5 flex items-center justify-between text-left"
              data-testid={`faq-${i}`}
            >
              <span className="font-bold text-[13px] text-emerald-900">{it.q}</span>
              {open === i ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            {open === i && <div className="px-3 pb-3 text-[12.5px] text-slate-700 leading-snug">{it.a}</div>}
          </div>
        ))}
      </div>
    </section>
  );
};

const LimitedOffer = ({ onOrderClick, srMode }) => (
  <section className="px-4 mt-6">
    <div className="bg-gradient-to-br from-emerald-900 to-emerald-700 text-white rounded-2xl p-4 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-400/30 rounded-full blur-2xl" />
      <div className="text-amber-300 text-[11px] tracking-[0.2em] font-bold uppercase">⏰ Limited Time</div>
      <h3 className="font-display text-2xl font-extrabold mt-1 leading-tight">
        Aaj ka offer khatam hone se pehle order karein
      </h3>
      <p className="text-emerald-50/90 text-[13px] mt-1.5">4 Bottles • ₹{PRICE} • Free Delivery • COD • 7-Day Refund</p>
      <button data-testid="limited-cta" onClick={onOrderClick} className="btn-primary w-full mt-3 text-base">
        Order Now — Cash on Delivery
      </button>
      <SecureBySr show={srMode} dark />
    </div>
  </section>
);

/* ------------------------- Footer with menu links ------------------------- */
const Footer = () => {
  const links = [
    { label: "Contact Us", to: "/contact" },
    { label: "Return Request", to: "/return-request" },
    { label: "Terms & Conditions", to: "/terms-conditions" },
    { label: "Privacy Policy", to: "/privacy-policy" },
    { label: "Shipping Policy", to: "/shipping-policy" },
    { label: "Complaint", to: "/complaint" },
  ];
  return (
    <footer className="px-4 mt-8 mb-28" data-testid="footer">
      <div className="card-soft p-4 text-center">
        <div className="font-display text-lg font-extrabold text-emerald-900">make2shine<sup className="text-[10px]">®</sup></div>
        <div className="text-[11px] text-slate-500">Premium Cleaning Expert</div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mt-3">
          {links.map((l) => (
            <Link key={l.to} to={l.to} className="text-[12px] text-emerald-700 underline underline-offset-2 font-semibold" data-testid={`footer-${l.label.toLowerCase().replace(/\s/g, "-")}`}>
              {l.label}
            </Link>
          ))}
        </div>
        <div className="text-[11px] text-slate-500 mt-3">
          © {new Date().getFullYear()} make2shine® • Support: 10am–7pm IST (Mon–Sat)
        </div>
      </div>
    </footer>
  );
};

const StickyCTA = ({ onOrderClick }) => (
  <div className="fixed bottom-0 left-0 right-0 z-30 pointer-events-none">
    <div className="max-w-[440px] mx-auto px-3 pb-3 pointer-events-auto">
      <div className="bg-white/95 backdrop-blur-md border border-slate-200 rounded-2xl shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.25)] px-3 py-2 flex items-center gap-2.5">
        <img
          src="/assets/product/hero-main.webp"
          alt=""
          width="48"
          height="48"
          loading="lazy"
          decoding="async"
          className="w-12 h-12 object-contain rounded-lg bg-emerald-50 border border-emerald-100 shrink-0"
        />
        <div className="leading-tight">
          <div className="text-[10px] text-slate-500 line-through">₹{MRP}</div>
          <div className="text-base font-extrabold text-emerald-900">₹{PRICE} <span className="text-[10px] font-semibold text-emerald-700">for 4 bottles</span></div>
        </div>
        <button onClick={onOrderClick} data-testid="sticky-cta" className="btn-primary flex-1 text-sm flex items-center justify-center gap-1 py-3 px-2">
          Order Now <ChevronRight size={16} />
        </button>
      </div>
    </div>
  </div>
);

/* ------------------------- Page ------------------------- */
export default function Landing() {
  const navigate = useNavigate();
  const viewFiredRef = useRef(false);
  useEffect(() => {
    if (viewFiredRef.current) return;
    viewFiredRef.current = true;
    fireCapi("ViewContent", {
      custom_data: { content_ids: ["SHINEX-599"], content_name: "Multifunctional Japanese Cleaner", value: PRICE, currency: "INR" },
    });
  }, []);

  const [srMode, setSrMode] = useState(false);
  const srBusyRef = useRef(false);
  useEffect(() => {
    getSrCheckoutConfig().then((c) => {
      if (c?.enabled) { setSrMode(true); loadSrSdk(c.sellerDomain).catch(() => {}); }
    });
  }, []);

  const addToCartFiredRef = useRef(false);
  const goOrder = (evt) => {
    if (!addToCartFiredRef.current) {
      addToCartFiredRef.current = true;
      fireCapi("AddToCart", { custom_data: { content_ids: ["SHINEX-599"], value: PRICE, currency: "INR" } });
    }
    if (srMode) {
      if (srBusyRef.current) return;
      srBusyRef.current = true;
      openSrCheckout(evt, () => { srBusyRef.current = false; })
        .catch(() => { srBusyRef.current = false; navigate("/order"); });
      return;
    }
    navigate("/order");
  };

  return (
    <div className="m2s-frame pb-20">
      <BrandHeader />
      <Hero onOrderClick={goOrder} srMode={srMode} />
      <CleaningDifference onOrderClick={goOrder} />
      <NewsLogos />
      <ExplainerVideo />
      <PackBox onOrderClick={goOrder} srMode={srMode} />
      <GuaranteeBar />
      <DeliveryEstimate />
      <AuthorizedSeller />
      <SoldStat />
      <Benefits />
      <Comparison />
      <HowToUse />
      <PackageReveal />
      <ReviewSection />
      <FAQ />
      <LimitedOffer onOrderClick={goOrder} srMode={srMode} />
      <Footer />
      <StickyCTA onOrderClick={goOrder} />
      <FloatingCallButton />
      <Link to="/order" className="hidden" prefetch="intent" />
    </div>
  );
}
